// NOTE: The intermediate flag-on state (lspay_cp_cnp_split_entry=true, lspay_cnp_payments=true)
// is not production-supported until the CP-modal CNP replacement and final QA are complete.
import { Button, Tooltip } from '@lightspeed/design-system-react'
import { ApiRetailerPaymentType } from '@vend/types'
import { T, useT } from '@transifex/react'
import React, { FunctionComponent } from 'react'
import { useLocalizedPaymentTypeName } from '@vend/react-utilities'
import {
  LSPayCardNotPresentEntryIcon,
  LSPayCardPresentEntryIcon,
} from './lspay-entry-icons'
import { useLSPayEntryPaymentActions } from './useLSPayEntryPaymentActions'

export interface LSPaySplitButtonProps {
  paymentType?: ApiRetailerPaymentType
  disabled?: boolean
  pay: (
    payment: ApiRetailerPaymentType,
    paymentData?: { lspayFlow: 'cnp' }
  ) => void
  amountTendered?: number | string
}

export const LSPaySplitButton: FunctionComponent<LSPaySplitButtonProps> = ({
  paymentType,
  disabled,
  pay,
  amountTendered,
}) => {
  const t = useT()
  const localizedPaymentTypeName = useLocalizedPaymentTypeName()
  const {
    handleCardNotPresentPayment,
    handleTerminalPayment,
    isCnpDisabled,
    isDisabled,
    isOffline,
    isAmountBelowMinimum,
    currencySymbol,
    minimumAmount,
  } = useLSPayEntryPaymentActions({
    paymentType,
    disabled,
    pay,
    amountTendered,
  })

  if (!paymentType) {
    return null
  }

  const paymentTypeName = localizedPaymentTypeName(paymentType.name)

  const showDisabledTooltip = isOffline || isAmountBelowMinimum

  const cardPresentLabel = t('{paymentTypeName} card reader', {
    _key: 'Payments.PayScreen.SplitButton.CardPresent.label',
    _context: 'Accessible label for the LSPay card reader split action',
    paymentTypeName,
  })
  const cardNotPresentLabel = t('{paymentTypeName} manual entry', {
    _key: 'Payments.PayScreen.SplitButton.CardNotPresent.label',
    _context: 'Accessible label for the LSPay manual entry split action',
    paymentTypeName,
  })

  const tooltipHeaderTexts = {
    belowMinAmount: t('Minimum payment amount required', {
      _key: 'Payments.PayScreen.SplitButton.Disabled.MinAmount.tooltipHeader',
      _context: 'tooltip header for lightspeed payments minimum amount',
    }),
    offline: t("You can't use this payment type while offline.", {
      _key: 'Payments.PayScreen.SplitButton.Disabled.Offline.tooltipHeader',
      _context: 'tooltip message for lightspeed payments when offline',
    }),
  }

  const tooltipMessageTexts = {
    belowMinAmount: (
      <T
        _str="Payment amount must be at least {minAmount}"
        _key="Payments.PayScreen.SplitButton.Disabled.MinAmount.tooltipMessage"
        _context="tooltip message for lightspeed payments minimum amount"
        minAmount={`${currencySymbol}${minimumAmount.toFixed(2)}`}
        lineBreak={<br />}
      />
    ),
    offline: (
      <T
        _str="You need to be connected to the internet to take payments using this payment type."
        _key="Payments.PayScreen.SplitButton.Disabled.Offline.tooltipMessage"
        _context="tooltip message for lightspeed payments when offline"
        lineBreak={<br />}
      />
    ),
    cardPresent: t('Tap, insert, or swipe card', {
      _key: 'Payments.PayScreen.SplitButton.CardPresent.tooltip',
      _context: 'Tooltip for the LSPay card reader split action',
    }),
    cardNotPresent: t('Enter card details manually', {
      _key: 'Payments.PayScreen.SplitButton.CardNotPresent.tooltip',
      _context: 'Tooltip for the LSPay manual entry split action',
    }),
    refund: t("This refund requires the customer's card to be present.", {
      _key: 'Payments.PayScreen.SplitButton.Disabled.Refund.tooltipMessage',
      _context:
        'Tooltip for the LSPay manual entry split action during a refund',
    }),
  }

  const isCnpDisabledForRefund = isCnpDisabled && !isDisabled

  const disabledMessage = isAmountBelowMinimum
    ? tooltipMessageTexts.belowMinAmount
    : tooltipMessageTexts.offline

  const activeTooltipHeader = showDisabledTooltip
    ? isAmountBelowMinimum
      ? tooltipHeaderTexts.belowMinAmount
      : tooltipHeaderTexts.offline
    : undefined

  const activeTooltipIcon = showDisabledTooltip ? 'info-circle' : undefined

  const cardPresentMessage = showDisabledTooltip
    ? disabledMessage
    : tooltipMessageTexts.cardPresent

  const cardNotPresentMessage = isCnpDisabledForRefund
    ? tooltipMessageTexts.refund
    : showDisabledTooltip
    ? disabledMessage
    : tooltipMessageTexts.cardNotPresent

  const cnpTooltipIcon = isCnpDisabledForRefund
    ? 'info-circle'
    : activeTooltipIcon

  return (
    <div className="lspay-split-button-group">
      <Tooltip
        direction="center"
        canBeShown={true}
        message={cardPresentMessage}
        header={activeTooltipHeader}
        icon={activeTooltipIcon}
      >
        <Button
          aria-label={cardPresentLabel}
          className="lspay-split-button lspay-split-button--card-present"
          disabled={isDisabled}
          onClick={handleTerminalPayment}
          variant="go"
          data-track={`payment-type-${paymentType.name}`}
          data-id={paymentType.type_id}
        >
          <LSPayCardPresentEntryIcon aria-hidden="true" />
          <span className="lspay-split-button__label">{paymentTypeName}</span>
        </Button>
      </Tooltip>
      <Tooltip
        direction="bottomRight"
        canBeShown={true}
        message={cardNotPresentMessage}
        header={isCnpDisabledForRefund ? undefined : activeTooltipHeader}
        icon={cnpTooltipIcon}
      >
        <Button
          aria-label={cardNotPresentLabel}
          className="lspay-split-button lspay-split-button--card-not-present"
          disabled={isCnpDisabled}
          onClick={handleCardNotPresentPayment}
          variant="go"
          data-track={`payment-type-${paymentType.name}`}
          data-id={paymentType.type_id}
        >
          <LSPayCardNotPresentEntryIcon aria-hidden="true" />
        </Button>
      </Tooltip>
    </div>
  )
}
